import { useCallback, useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { useQueryState } from 'nuqs'

import {
  useFacilityFloorsDrawerLazyQuery,
  useFacilityTourPlansLazyQuery,
  useFloorDevicesCountQuery,
  useReorderFloorsMutation,
} from '@/graphql/generated/hooks'
import {
  selectFacilityTabBarCount,
  setFacilityTabBarCount,
} from '@/redux/ui/uiSlice'

import { FloorI, TourPlanI, UseFacilityI } from '../types/types'
import {
  mapFloor,
  mapTourPlans,
  sortFloorsByDisplayOrderThenName,
} from '../utils/mappedFacilityData'

export function useFacilityData(facilityId: string): UseFacilityI {
  const dispatch = useDispatch()
  const { floorCount, placedDeviceCount, checkpointCount, tourPlansCount } =
    useSelector(selectFacilityTabBarCount)
  const [floorId, setFloorId] = useQueryState('floorId')
  const [floors, setFloors] = useState<FloorI[]>()
  const [tourPlans, setTourPlans] = useState<TourPlanI[]>()
  const { data: deviceCountData } = useFloorDevicesCountQuery({
    variables: { floorId },
  })
  const [getTourPlans, { data: tourPlansData }] = useFacilityTourPlansLazyQuery(
    {
      variables: { facilityId },
    }
  )
  const [getFloors, { data: facilityFloorsData }] =
    useFacilityFloorsDrawerLazyQuery({
      variables: { facilityId },
      fetchPolicy: 'network-only',
    })

  const [reorderFloorsMutation] = useReorderFloorsMutation()

  const reorderFloors = useCallback(
    (floorIds: string[]) => {
      if (!floors || !facilityId) return

      // Optimistic update - reorder local state immediately
      const reorderedFloors = floorIds
        .map((id) => floors.find((f) => f.id === id))
        .filter((f): f is FloorI => f !== undefined)

      setFloors(reorderedFloors)

      // Call the mutation
      reorderFloorsMutation({
        variables: {
          input: {
            facilityId,
            floorIds,
          },
        },
      })
    },
    [floors, facilityId, reorderFloorsMutation]
  )

  useEffect(() => {
    if (facilityFloorsData) {
      const defaultFloorId = facilityFloorsData?.facility?.defaultFloorId
      const floors = facilityFloorsData?.facility?.floors?.edges
        ?.map((e) => e.node)
        .sort(sortFloorsByDisplayOrderThenName)
        .map((floor) => mapFloor(floor, defaultFloorId))

      setFloors(floors)
      dispatch(
        setFacilityTabBarCount({
          floorCount: floors?.length,
        })
      )
    }
  }, [facilityFloorsData])

  useEffect(() => {
    if (floorId) {
      localStorage.setItem('floorId', floorId as string)
    } else {
      const storageFloorId = localStorage.getItem('floorId')
      setFloorId(storageFloorId)
    }
  }, [floorId])

  useEffect(() => {
    if (facilityId) {
      getFloors()
      getTourPlans()
    }
  }, [facilityId])

  useEffect(() => {
    if (tourPlansData) {
      setTourPlans(mapTourPlans(tourPlansData?.tourPlans))
      dispatch(
        setFacilityTabBarCount({
          tourPlansCount: tourPlansData?.tourPlans?.totalCount,
        })
      )
    }
  }, [tourPlansData])

  useEffect(() => {
    if (deviceCountData) {
      dispatch(
        setFacilityTabBarCount({
          checkpointCount: deviceCountData?.floor?.checkpoints?.totalCount,
          placedDeviceCount: deviceCountData?.floor?.devices?.totalCount,
        })
      )
    }
  }, [deviceCountData])

  useEffect(
    () => () => {
      dispatch(
        setFacilityTabBarCount({
          floorCount: 0,
          placedDeviceCount: 0,
          checkpointCount: 0,
          incidentsCount: 0,
          tourPlansCount: 0,
        })
      )
    },
    []
  )

  return {
    currentFloorId: floorId,
    setCurrentFloorId: setFloorId,
    floors,
    placedDeviceCount,
    floorCount,
    checkpointCount,
    tourPlansCount,
    tourPlans,
    reorderFloors,
  }
}
